import React, { useEffect, useState } from 'react';
import api from '../../utils/api';

function StarRating({ rating, size = 14 }) {
  return (
    <div style={{ display: 'flex', gap: 1 }}>
      {[1,2,3,4,5].map(s => (
        <span key={s} style={{ fontSize: size, color: s <= rating ? '#f5c400' : 'var(--border)' }}>★</span>
      ))}
    </div>
  );
}

const STATUS_STYLES = {
  pending:  { bg: 'rgba(245,196,0,0.12)',  color: '#f5c400',  label: '⏳ Pending'  },
  approved: { bg: 'rgba(16,185,129,0.12)', color: '#10b981', label: '✅ Approved' },
  rejected: { bg: 'rgba(239,68,68,0.12)',  color: '#ef4444',  label: '❌ Rejected' },
};

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [updating, setUpdating] = useState({});

  const load = () => api.get('/reviews/admin').then(({ data }) => setReviews(data.reviews || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    setUpdating(u => ({ ...u, [id]: true }));
    try {
      await api.put(`/reviews/${id}/status`, { status });
      setReviews(reviews.map(r => r._id === id ? { ...r, status } : r));
    } catch { alert('Error updating review'); }
    finally { setUpdating(u => ({ ...u, [id]: false })); }
  };

  const deleteReview = async (id) => {
    if (!window.confirm('Delete this review permanently?')) return;
    await api.delete(`/reviews/${id}`);
    setReviews(reviews.filter(r => r._id !== id));
  };

  const filtered = reviews.filter(r => filter === 'all' || r.status === filter);
  const counts = { all: reviews.length, pending: reviews.filter(r => r.status === 'pending').length, approved: reviews.filter(r => r.status === 'approved').length, rejected: reviews.filter(r => r.status === 'rejected').length };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24 }}>REVIEWS</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>Approve or reject user reviews before they go public</p>
        </div>
        <div style={{ background: 'rgba(245,196,0,0.1)', border: '1px solid rgba(245,196,0,0.3)', borderRadius: 10, padding: '8px 16px', fontSize: 14, color: '#f5c400', fontWeight: 700 }}>
          {counts.pending} pending
        </div>
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {Object.entries(counts).map(([key, count]) => (
          <button key={key} onClick={() => setFilter(key)} className={`btn btn-sm ${filter === key ? 'btn-primary' : 'btn-secondary'}`} style={{ textTransform: 'capitalize' }}>
            {key} ({count})
          </button>
        ))}
      </div>

      {loading ? <div className="loading-spinner"><div className="spinner" /></div> :
        filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⭐</div>
            <p>No {filter} reviews</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {filtered.map(r => (
              <div key={r._id} className="card" style={{ borderLeft: `3px solid ${STATUS_STYLES[r.status]?.color}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 42, height: 42, borderRadius: '50%', background: 'var(--gradient-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 18, color: '#0a0900', flexShrink: 0 }}>{r.user?.username?.[0]?.toUpperCase()}</div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15 }}>{r.user?.username} <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: 13 }}>({r.user?.email})</span></div>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 3 }}>
                        <StarRating rating={r.rating} />
                        {r.platform && <span style={{ fontSize: 12, background: 'var(--bg-secondary)', padding: '1px 8px', borderRadius: 6, color: 'var(--text-secondary)' }}>{r.platform.name}</span>}
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{new Date(r.createdAt).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: STATUS_STYLES[r.status]?.bg, color: STATUS_STYLES[r.status]?.color }}>{STATUS_STYLES[r.status]?.label}</span>
                </div>

                <div style={{ margin: '14px 0', padding: '14px 16px', background: 'var(--bg-secondary)', borderRadius: 10 }}>
                  {r.title && <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>{r.title}</div>}
                  <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6, margin: 0 }}>{r.content}</p>
                </div>

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {r.status !== 'approved' && (
                    <button className="btn btn-sm" onClick={() => updateStatus(r._id, 'approved')} disabled={updating[r._id]} style={{ background: 'rgba(16,185,129,0.15)', color: '#10b981', border: '1px solid #10b981' }}>
                      {updating[r._id] ? '...' : '✅ Approve & Publish'}
                    </button>
                  )}
                  {r.status !== 'rejected' && (
                    <button className="btn btn-sm" onClick={() => updateStatus(r._id, 'rejected')} disabled={updating[r._id]} style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid #ef4444' }}>
                      {updating[r._id] ? '...' : '❌ Reject'}
                    </button>
                  )}
                  {r.status === 'approved' && (
                    <button className="btn btn-sm" onClick={() => updateStatus(r._id, 'pending')} disabled={updating[r._id]} style={{ background: 'rgba(245,196,0,0.1)', color: '#f5c400', border: '1px solid #f5c400' }}>
                      ↩ Unpublish
                    </button>
                  )}
                  <button className="btn btn-danger btn-sm" onClick={() => deleteReview(r._id)}>🗑 Delete</button>
                </div>
              </div>
            ))}
          </div>
        )
      }
    </div>
  );
}
